import { CheckResult, PrintedCheck, checkPrinted, checksForFixture, isPrintedCheck } from '@/reports/bundles/expectations';
import { fixtureInput } from '@/reports/bundles/fixtureInput';
import { CompiledReportBundle, FixtureResult, REPORT_BUNDLE_FIXTURE_DIR, ReportBundleFiles } from '@/reports/bundles/types';
import { RunReportContext } from '@/reports/types';
import { drawDocument, moduleFor } from './drawDocument';

/**
 * The checks the compile cannot make: those on the printed document, made by
 * drawing each fixture's document as it prints and reading it back. Only the
 * fixtures that hold one are drawn. What comes back belongs beside the
 * compile's own results (`withPrinted`).
 */
export async function checkFixturesPrinted(compiled: CompiledReportBundle, files: ReportBundleFiles): Promise<Record<string, CheckResult[]>> {
  const out: Record<string, CheckResult[]> = {};
  const fixtures = Object.keys(files).filter((p) => p.startsWith(REPORT_BUNDLE_FIXTURE_DIR) && p.endsWith('.json')).sort();
  for (const file of fixtures) {
    let raw: unknown;
    try {
      raw = JSON.parse(files[file]);
    } catch {
      continue; // the compile says so
    }
    const printed = checksForFixture(file, raw, files).planned.filter((p): p is { check: PrintedCheck; from: string } => isPrintedCheck(p.check));
    if (printed.length === 0) continue;
    const missAll = (message: string) => printed.map((p) => ({ check: p.check, from: p.from, ok: false, message }));
    try {
      const module = moduleFor(compiled);
      const { definition } = module;
      const input = fixtureInput(compiled, file, files[file]);
      const context = input.context as RunReportContext & { settings?: unknown };
      if (compiled.hasSettings && definition.normalizeSettings) context.settings = definition.normalizeSettings(input.settings ?? {});
      const values = definition.normalizeValues(input.values ?? definition.defaultValues());
      const model = definition.evaluate(values, context);
      const drawn = await drawDocument(module, values, model, context, (host) => printed.map((p) => ({ check: p.check, from: p.from, ...checkPrinted(host, p.check) })));
      out[file] = drawn.value ?? missAll(drawn.error ?? '');
    } catch (error) {
      out[file] = missAll(error instanceof Error ? error.message : String(error));
    }
  }
  return out;
}

/** The compile's results with the printed document's checks beside each fixture's own: a fixture holds only when all of them do. */
export function withPrinted(results: FixtureResult[], printed: Record<string, CheckResult[]>, pending = false): Array<FixtureResult & { pending?: boolean }> {
  return results.map((result) => {
    const made = printed[result.file];
    if (!made) return (result.printed ?? 0) > 0 && pending ? { ...result, pending: true } : result;
    const checks = [...(result.checks ?? []), ...made];
    const missed = checks.filter((c) => !c.ok).length;
    const ok = result.ok && made.every((c) => c.ok);
    if (ok) return { ...result, checks };
    // the compile counted only its own checks: count them all again
    const verdict = result.error?.replace(/(^|; )\d+ of its \d+ checks missed$/, '') || null;
    return { ...result, checks, ok, error: [verdict, missed ? `${missed} of its ${checks.length} checks missed` : null].filter(Boolean).join('; ') };
  });
}
